// Client for account insights built from run history. Backend: api/core/routers/insights.py.
// Account owners see their own data; admins can pass customerId.

import { ApiError, TokenGetter } from './learningApi';

export type ErrorWindow = '24h' | '7d' | '30d';

export type ErrorKind =
  | 'time_limit'
  | 'rate_limit'
  | 'model_unavailable'
  | 'invalid_output'
  | 'auth'
  | 'input_too_large'
  | 'content_policy'
  | 'bad_input'
  | 'network'
  | 'other';

export interface ErrorTarget {
  type: 'function' | 'chain';
  key: string;
  /** Function: newest version id (null if the function no longer exists). Chain: chain id. */
  id: string | null;
  name: string;
  count: number;
}

export interface ErrorGroup {
  id: string;
  kind: ErrorKind;
  label: string;
  count: number;
  share: number | null;
  first_seen: string | null;
  last_seen: string | null;
  /** Counts per bucket of `axis`. */
  series: number[];
  targets: ErrorTarget[];
  more_targets: number;
  examples: { type: 'function' | 'chain'; id: string }[];
}

export interface ErrorsReport {
  window: { key: ErrorWindow; unit: 'hour' | 'day'; since: string; until: string };
  axis: string[];
  totals: { function_runs: number; function_failed: number; chain_runs: number; chain_failed: number; failed: number };
  kinds: { kind: ErrorKind; count: number }[];
  groups: ErrorGroup[];
  more_groups: number;
  rescued: { runs: number; kinds: { kind: ErrorKind; count: number }[]; groups: ErrorGroup[]; more_groups: number } | null;
}

export interface SpendSource {
  /** 'tests': test runs and their AI judging, billed but not part of any function's traffic. */
  type: 'function' | 'chain' | 'tests';
  name: string;
  id: string | null;
  credits: number;
  runs: number;
  share: number | null;
  previous_credits: number;
  previous_runs: number;
}

export interface SpendModel {
  model: string;
  credits: number;
  runs: number;
  share: number | null;
  previous_credits: number;
  previous_runs: number;
}

export interface SpendDay {
  day: number;
  date: string;
  current: number | null;
  previous: number | null;
  sources?: Record<string, number>;
  other?: number;
}

export interface SpendReport {
  now: string;
  month: { label: string; start: string; end: string };
  previous_month: { label: string; start: string; end: string };
  totals: {
    mtd: number;
    runs_mtd: number;
    previous_month: number;
    previous_same_point: number;
    last_7_full_days: number;
    change_vs_same_point: number | null;
  };
  projection: {
    days_in_month: number;
    elapsed_days: number;
    remaining_days: number;
    run_rate: { daily: number; projected: number };
    naive: { daily: number | null; projected: number | null };
    change_vs_previous_month: number | null;
  };
  allowance: number | null;
  billing_period: { since: string; credits: number } | null;
  top_sources: { key: string; type: 'function' | 'chain' | 'tests'; name: string; id: string | null }[];
  daily: SpendDay[];
  sources: SpendSource[];
  models: SpendModel[];
}

export interface SideMetrics {
  runs: number;
  success_rate: number | null;
  backup_share: number | null;
  invalid_output_share: number | null;
  p95_ms: number | null;
  credits: number;
  credits_per_1k_runs: number | null;
}

export type ComparedMetric = 'success_rate' | 'backup_share' | 'invalid_output_share' | 'p95_ms' | 'credits_per_1k_runs';

export interface MetricComparison {
  metric: ComparedMetric;
  baseline: number | null;
  treated: number | null;
  delta: number | null;
  relative: number | null;
  direction: 'better' | 'worse' | 'same' | null;
}

export interface LearningTarget {
  type: 'function' | 'chain';
  /** Learning target id: function original_uuid or chain id. */
  id: string;
  /** Newest function version id, or the chain id. */
  performance_id: string;
  name: string;
  enabled: boolean;
  entries_active: number;
  entries_waiting: number;
  knowledge_published_at: string | null;
  first_activated_at: string | null;
  /** with_without: runs that got learned knowledge vs runs that didn't. before_after: equal windows around first activation. */
  method: 'with_without' | 'before_after' | 'none';
  window: { since: string; until: string; cut?: string };
  baseline: SideMetrics;
  treated: SideMetrics;
  comparison: { enough: boolean; metrics: MetricComparison[] };
}

export interface LearningImpactReport {
  window: { days: number; since: string; until: string };
  min_runs: number;
  targets: LearningTarget[];
}

type Params = Record<string, string | number | boolean | null | undefined>;

function query(params: Params): string {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function insightsApi(getToken: TokenGetter) {
  async function get<T>(path: string): Promise<T> {
    const token = await getToken();
    const res = await fetch(`/api/v2/insights${path}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = await res.json();
        message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail || body);
      } catch {
        /* keep statusText */
      }
      throw new ApiError(message, res.status);
    }
    return res.json();
  }

  return {
    errors: (window: ErrorWindow, includeRescued: boolean, customerId?: string | null) =>
      get<ErrorsReport>(`/errors${query({ window, include_rescued: includeRescued || undefined, customer_id: customerId })}`),
    spend: (customerId?: string | null) => get<SpendReport>(`/spend${query({ customer_id: customerId })}`),
    learningImpact: (days: number, customerId?: string | null) =>
      get<LearningImpactReport>(`/learning-impact${query({ days, customer_id: customerId })}`),
  };
}
